import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ChevronRight, LayoutDashboard, Package, RotateCcw, UserCog, MapPin, LogOut, CheckCircle2, User, Star, Car
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import DashboardSection from "./account/DashboardSection";
import OrdersSection from "./account/OrdersSection";
import ReturnsSection from "./account/ReturnsSection";
import ProfileSection from "./account/ProfileSection";
import AddressesSection from "./account/AddressesSection";
import ReviewsSection from "./account/ReviewsSection";
import GarageSection from "./account/GarageSection";

const NAV = [
  { key: "dashboard", label: "Tableau de bord", icon: LayoutDashboard },
  { key: "orders", label: "Mes commandes", icon: Package },
  { key: "returns", label: "Mes retours", icon: RotateCcw },
  { key: "garage", label: "Mon garage", icon: Car },
  { key: "reviews", label: "Mes avis", icon: Star },
  { key: "profile", label: "Mes informations", icon: UserCog },
  { key: "addresses", label: "Mes adresses", icon: MapPin },
];

const AccountPage = () => {
  const { user, authReady, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const justOrdered = searchParams.get("commande") === "ok";
  const initialSection = searchParams.get("section") || (justOrdered ? "orders" : "dashboard");
  const [section, setSection] = useState(initialSection);
  const [preselectOrder, setPreselectOrder] = useState(null);

  useEffect(() => {
    if (authReady && !user) navigate("/connexion");
  }, [authReady, user, navigate]);

  if (!user) return null;

  const requestReturn = (order) => { setPreselectOrder(order); setSection("returns"); };

  return (
    <div className="max-w-[1440px] mx-auto px-5 py-8">
      <div className="flex items-center gap-1.5 text-[14px] text-slate-400 mb-5">
        <Link to="/" className="hover:text-[#FF3B3B]">Accueil</Link>
        <ChevronRight size={15} />
        <span className="text-slate-50 font-semibold">Mon compte</span>
      </div>

      {justOrdered && (
        <div className="bg-emerald-950/40 border border-emerald-800/50 text-emerald-300 rounded-2xl p-5 mb-6 flex items-center gap-3">
          <CheckCircle2 size={26} className="text-emerald-400 shrink-0" />
          <div>
            <div className="font-bold">Commande confirmée&nbsp;!</div>
            <div className="text-[14px]">Merci pour votre achat. Vous recevrez un email de confirmation sous peu.</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Sidebar */}
        <aside className="lg:col-span-1">
          <div className="bg-[#10131A] rounded-2xl border border-[#1E232D] p-4 lg:sticky lg:top-24">
            <div className="flex items-center gap-3 px-2 py-3 mb-2 border-b border-[#1E232D]">
              <div className="w-11 h-11 rounded-full bg-[#181C26] flex items-center justify-center shrink-0"><User size={22} className="text-white" /></div>
              <div className="min-w-0">
                <div className="font-bold text-slate-50 truncate">{user.name}</div>
                <div className="text-[12px] text-slate-400 truncate">{user.email}</div>
              </div>
            </div>
            <nav className="mt-2 space-y-1">
              {NAV.map((n) => (
                <button
                  key={n.key}
                  data-testid={`account-nav-${n.key}`}
                  onClick={() => setSection(n.key)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[15px] font-medium transition-colors ${section === n.key ? "bg-[#E01F1F] text-white" : "text-slate-300 hover:bg-[#222735]"}`}
                >
                  <n.icon size={18} /> {n.label}
                </button>
              ))}
              <button onClick={() => { logout(); navigate("/"); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[15px] font-medium text-slate-300 hover:bg-[#222735] transition-colors">
                <LogOut size={18} /> Se déconnecter
              </button>
            </nav>
          </div>
        </aside>

        {/* Content */}
        <div className="lg:col-span-3">
          {section === "dashboard" && <DashboardSection setSection={setSection} />}
          {section === "orders" && <OrdersSection onRequestReturn={requestReturn} />}
          {section === "returns" && <ReturnsSection preselectOrder={preselectOrder} clearPreselect={() => setPreselectOrder(null)} />}
          {section === "profile" && <ProfileSection />}
          {section === "addresses" && <AddressesSection />}
          {section === "reviews" && <ReviewsSection />}
          {section === "garage" && <GarageSection />}
        </div>
      </div>
    </div>
  );
};

export default AccountPage;
